import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingBag, BarChart2, Wallet,
  Star, Settings, Store, Menu, X, LogOut, ChevronRight, AlertTriangle,
  Bell, Search,
} from 'lucide-react';
import { useVendorStore } from '@/store/vendor-store';
import { vendorApi } from '@/services/api';
import { cn } from '@nepal-ecom/ui';
import { Avatar, AvatarFallback } from '@nepal-ecom/ui';
import { Badge } from '@nepal-ecom/ui';
import { Button } from '@nepal-ecom/ui';
import { Input } from '@nepal-ecom/ui';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@nepal-ecom/ui';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/store', icon: Store, label: 'My Store' },
  { to: '/products', icon: Package, label: 'Products' },
  { to: '/orders', icon: ShoppingBag, label: 'Orders' },
  { to: '/analytics', icon: BarChart2, label: 'Analytics' },
  { to: '/finance', icon: Wallet, label: 'Finance' },
  { to: '/finance/bank-accounts', icon: Wallet, label: 'Bank accounts' },
  { to: '/reviews', icon: Star, label: 'Reviews' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

function KycBanner({ status }: { status: string }) {
  // State-specific copy so pending / under-review / rejected / suspended
  // vendors each get accurate guidance instead of one generic message.
  const config: Record<
    string,
    { tone: 'amber' | 'red'; message: string; cta?: { label: string; href: string } }
  > = {
    pending: {
      tone: 'amber',
      message: 'Your store is pending review. Submit your KYC documents to start selling.',
      cta: { label: 'Complete KYC', href: '/store' },
    },
    under_review: {
      tone: 'amber',
      message: 'Your KYC is under review. We’ll email you once it’s approved — usually within 1–2 business days.',
    },
    rejected: {
      tone: 'red',
      message: 'Your KYC was rejected. Review the reason, update your documents, and resubmit.',
      cta: { label: 'Update & resubmit', href: '/store' },
    },
    suspended: {
      tone: 'red',
      message: 'Your store has been suspended. Contact support to resolve this.',
      cta: { label: 'Contact support', href: '/settings' },
    },
  };
  const c = config[status] ?? config.pending;
  const isRed = c.tone === 'red';

  return (
    <div
      className={cn(
        'border-b px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm',
        isRed
          ? 'bg-gradient-to-r from-red-50 via-red-50 to-red-100/50 border-red-200'
          : 'bg-gradient-to-r from-amber-50 via-amber-50 to-amber-100/50 border-amber-200',
      )}
    >
      <div
        className={cn(
          'h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0',
          isRed ? 'bg-red-100' : 'bg-amber-100',
        )}
      >
        <AlertTriangle className={cn('h-4 w-4', isRed ? 'text-red-600' : 'text-amber-600')} />
      </div>
      <span className={cn('font-medium', isRed ? 'text-red-900' : 'text-amber-900')}>
        {c.message}
      </span>
      {c.cta && (
        <a
          href={c.cta.href}
          className={cn(
            'ml-auto flex-shrink-0 inline-flex items-center gap-1 font-semibold hover:underline text-xs',
            isRed ? 'text-red-700 hover:text-red-900' : 'text-amber-700 hover:text-amber-900',
          )}
        >
          {c.cta.label} <ChevronRight className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const variant = tier === 'platinum' ? 'warning' : tier === 'gold' ? 'warning' : tier === 'silver' ? 'info' : 'secondary';
  return <Badge variant={variant} className="capitalize text-[10px]">{tier}</Badge>;
}

function Sidebar({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { vendor } = useVendorStore();

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={cn(
          'sidebar-surface fixed inset-y-0 left-0 w-64 text-slate-300 flex flex-col z-50 transition-transform duration-200 ease-out',
          'lg:translate-x-0 lg:static lg:z-auto',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="px-5 py-4 border-b border-white/5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-xs">
                  {vendor?.storeName?.[0]?.toUpperCase() || 'S'}
                </div>
                <p className="font-semibold text-white text-sm truncate">
                  {vendor?.storeName || 'My Store'}
                </p>
              </div>
              <TierBadge tier={vendor?.tier || 'basic'} />
            </div>
            <button onClick={onClose} className="lg:hidden p-1 text-slate-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-hide">
          <p className="px-3 mb-2 text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em]">
            Workspace
          </p>
          <div className="space-y-0.5">
            {NAV_ITEMS.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary-600/15 text-white shadow-[inset_2px_0_0_theme(colors.primary.500)]'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="px-3 pb-3">
          <div className="rounded-xl bg-gradient-to-br from-primary-600/20 to-primary-800/30 border border-primary-500/20 p-4 text-xs">
            <p className="font-semibold text-white mb-1">Upgrade to Pro</p>
            <p className="text-slate-300 leading-relaxed mb-2">
              Lower commission, priority listing, and advanced analytics.
            </p>
            <Button size="sm" className="w-full bg-primary-500 hover:bg-primary-400">
              Learn more
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { vendor, logout } = useVendorStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="h-[60px] surface-glass border-b border-slate-100 flex items-center gap-3 px-4 sm:px-6 flex-shrink-0 sticky top-0 z-30">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-600 hover:bg-slate-100"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search orders, products…"
          className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
        />
      </div>

      <div className="flex-1" />

      <Button variant="ghost" size="icon" className="relative">
        <Bell className="h-[18px] w-[18px]" />
        <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-primary-500 rounded-full ring-2 ring-white" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-slate-100 transition-colors">
            <Avatar className="h-8 w-8">
              <AvatarFallback>{vendor?.name?.[0]?.toUpperCase() || 'V'}</AvatarFallback>
            </Avatar>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-slate-900 leading-tight">{vendor?.name || 'Vendor'}</p>
              <p className="text-[11px] text-slate-500 leading-tight truncate max-w-[140px]">{vendor?.email}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem><Settings className="h-4 w-4" /> Settings</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700 focus:bg-red-50">
            <LogOut className="h-4 w-4" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { vendor } = useVendorStore();
  const { data: vendorData } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const res = await vendorApi.getMyVendor();
      return res.data.data as any;
    },
  });
  const verificationStatus =
    vendorData?.verificationStatus ?? vendor?.kycStatus ?? 'pending';
  const isKycPending = verificationStatus !== 'approved';

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />
        {isKycPending && <KycBanner status={verificationStatus} />}

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
